import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ProfileRow, SupportedLanguageCode } from '@/types/supabase';
import type { PatchProfileInput } from '@/schemas/profile';
import { TERMS_VERSION } from '@/schemas/orders';
import { useAuth } from '@/hooks/useAuth';

const PROFILE_QUERY_KEY = ['profile'] as const;

async function fetchOwnProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export function useProfile() {
  const { user, initializing } = useAuth();
  return useQuery({
    queryKey: [...PROFILE_QUERY_KEY, user?.id ?? null] as const,
    enabled: !initializing && !!user,
    queryFn: () => fetchOwnProfile(user!.id),
    staleTime: 30_000,
  });
}

export function useUpdateLanguage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (language: SupportedLanguageCode) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('profiles').update({ language }).eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (data: PatchProfileInput) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('profiles').update(data).eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useAcceptTerms() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update({ terms_accepted_at: new Date().toISOString(), terms_version: TERMS_VERSION })
        .eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useUploadTaxCertificate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('Not authenticated');
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/me/tax-certificate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: 'Upload failed' }))) as { error?: string };
        throw new Error(body.error ?? 'Upload failed');
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}
